<template>
  <button
    v-if="isCopilotEnabled && liveSentiment"
    type="button"
    class="sentiment-chip"
    :class="`sentiment-chip--${liveSentiment.sentiment}`"
    :title="chipTitle"
    @click="openCopilot"
  >
    <span class="sentiment-chip__dot" />
    {{ label }}
  </button>
</template>

<script>
import { mapGetters } from 'vuex';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Chip de sentimento ao vivo no compositor (ao lado do Enviar). Lê a fonte
// única no store (copilot/getLiveSentiment) e, ao clicar, abre o Copiloto.
export default {
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return { liveTimer: null };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentAccountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      getLiveSentiment: 'copilot/getLiveSentiment',
    }),
    isCopilotEnabled() {
      return this.isFeatureEnabledonAccount(this.currentAccountId, 'copilot');
    },
    liveSentiment() {
      return this.getLiveSentiment(this.conversationId);
    },
    label() {
      const key = this.liveSentiment.sentiment.toUpperCase();
      return this.$t(`CONVERSATION_SIDEBAR.COPILOT.SENTIMENT_${key}`);
    },
    chipTitle() {
      const open = this.$t('CONVERSATION_SIDEBAR.COPILOT.CHIP_OPEN');
      return `${this.label} · ${this.liveSentiment.score}/100 · ${open}`;
    },
    // Última mensagem real — reativo às novas mensagens (websocket).
    lastMessageId() {
      const messages = this.currentChat?.messages || [];
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const type = messages[i].message_type;
        if (type === 0 || type === 1) return messages[i].id;
      }
      return null;
    },
  },
  watch: {
    conversationId() {
      this.fetchLive();
    },
    lastMessageId(newId, oldId) {
      if (!newId || newId === oldId) return;
      if (this.liveTimer) clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(() => this.fetchLive(), 1500);
    },
  },
  mounted() {
    this.fetchLive();
  },
  beforeDestroy() {
    if (this.liveTimer) clearTimeout(this.liveTimer);
  },
  methods: {
    fetchLive() {
      if (!this.isCopilotEnabled || !this.conversationId) return;
      this.$store.dispatch('copilot/fetchLiveSentiment', {
        conversationId: this.conversationId,
        lastMessageId: this.lastMessageId,
      });
    },
    openCopilot() {
      bus.$emit(BUS_EVENTS.OPEN_COPILOT);
    },
  },
};
</script>

<style lang="scss" scoped>
.sentiment-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-smaller);
  height: var(--space-large);
  padding: 0 var(--space-small);
  border: 0;
  border-radius: var(--border-radius-normal);
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-medium);
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  background: var(--s-50);
  color: var(--s-700);

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    flex: none;
  }

  &--positive {
    background: rgba(31, 157, 87, 0.1);
    color: #1f9d57;
  }
  &--neutral {
    background: var(--s-50);
    color: var(--s-700);
  }
  &--negative {
    background: rgba(229, 72, 77, 0.1);
    color: #e5484d;
  }

  &:hover {
    filter: brightness(0.97);
  }
}
</style>
